/**
 * White-label brand configuration.
 *
 * Controls brand name, logo, colors, and labels across the entire widget.
 * Switch brands via VITE_BRAND env var or URL param ?brand=indigene.
 *
 * Adding a new brand:
 * 1. Add logo to public/assets/
 * 2. Add a BrandConfig entry to BRANDS below
 * 3. Set VITE_BRAND=<key> or use ?brand=<key>
 */

// ---------------------------------------------------------------------------
// Brand config interface
// ---------------------------------------------------------------------------

export interface BrandConfig {
  /** Brand key (used in env var and URL param) */
  key: string;
  /** Display name shown in UI */
  name: string;
  /** Subtitle on login screen */
  subtitle: string;
  /** Path to horizontal logo (for headers) */
  logoHorizontal: string;
  /** Path to primary/stacked logo (for login screen) */
  logoPrimary: string;
  /** Primary brand color (headings, accents) */
  colorPrimary: string;
  /** Light tint of primary (active backgrounds) */
  colorPrimaryLight: string;
  /** Mid-tone purple for interactive elements (chat bubbles, chips) */
  colorPrimaryMid: string;
  /** Secondary/accent color (success states, highlights) */
  colorAccent: string;
  /** Light tint of accent */
  colorAccentLight: string;
  /** Spinner/loading label */
  thinkingLabel: string;
  /** Word Add-in manifest display name */
  manifestName: string;
}

// ---------------------------------------------------------------------------
// Brand definitions
// ---------------------------------------------------------------------------

const BRANDS: Record<string, BrandConfig> = {
  asthra: {
    key: "asthra",
    name: "Asthra AI",
    subtitle: "Regulatory Writing Co-Pilot",
    logoHorizontal: "/assets/asthra-logo-horizontal.png",
    logoPrimary: "/assets/asthra-logo-primary.png",
    colorPrimary: "#4C338A", // Legacy deep purple
    colorPrimaryLight: "#F3EDFC",
    colorPrimaryMid: "#8367C7", // Legacy interactive purple (bubbles, chips)
    colorAccent: "#52C41A", // Legacy green
    colorAccentLight: "#C2F8CB",
    thinkingLabel: "Asthra is thinking...",
    manifestName: "Asthra Widget",
  },
  indigene: {
    key: "indigene",
    name: "INDEGENE - NEXT MEDTECH DOC SUITE",
    subtitle: "Regulatory Document Automation",
    logoHorizontal: "/assets/indigene-logo.png",
    logoPrimary: "/assets/indigene-logo.png",
    colorPrimary: "#0067B1", // Indegene blue
    colorPrimaryLight: "#EBF4FB",
    colorPrimaryMid: "#4A9ED6", // Indegene mid blue
    colorAccent: "#E5007D", // Indegene magenta
    colorAccentLight: "#FDE8F2",
    thinkingLabel: "Processing...",
    manifestName: "INDEGENE - NEXT MEDTECH DOC SUITE",
  },
};

// ---------------------------------------------------------------------------
// Brand resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the active brand from env var or URL param.
 *
 * Priority:
 * 1. URL param ?brand=indigene
 * 2. VITE_BRAND env var
 * 3. Default: asthra
 */
function resolveBrand(): BrandConfig {
  // Check URL param
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const urlBrand = params.get("brand");
    if (urlBrand && BRANDS[urlBrand.toLowerCase()]) {
      return BRANDS[urlBrand.toLowerCase()];
    }
  }

  // Check env var
  const envBrand = import.meta.env.VITE_BRAND;
  if (envBrand && BRANDS[envBrand.toLowerCase()]) {
    return BRANDS[envBrand.toLowerCase()];
  }

  return BRANDS.asthra;
}

/** Active brand config — resolved once at module load */
export const brand: BrandConfig = resolveBrand();

/** All available brands (for settings UI if needed) */
export const availableBrands = Object.values(BRANDS);
